/**
 * Maps GET /api/ninja/applicant_account_requests/:id into UI groups.
 * Behavior mirrors Angular `bank-linking-logs.mapper.ts` (not a line-for-line port).
 */

import type {
  BankLinkingLogRow,
  BankLinkingRequestGroup,
  BankLinkResetType,
} from "../types/utils";

export type {
  BankLinkingLogRow,
  BankLinkingRequestGroup,
  BankLinkResetType,
} from "../types/utils";

/** Matches backend NinjaApplicantAccount::MAX_RETRY_COUNT. */
export const MAX_POLL_ATTEMPTS = 10;

const SMALL_WORDS = new Set([
  "and",
  "or",
  "the",
  "of",
  "in",
  "for",
  "to",
  "a",
  "an",
  "at",
  "by",
  "as",
]);

function titleCaseWords(words: string[]): string {
  return words
    .map((word, i) => {
      const lw = word.toLowerCase();
      if (i > 0 && SMALL_WORDS.has(lw)) return lw;
      return lw.charAt(0).toUpperCase() + lw.slice(1);
    })
    .join(" ");
}

function formatStepLabel(step: string): string {
  return titleCaseWords(step.split("_").filter(Boolean));
}

function formatLabelFromMixedString(value: string): string {
  const normalized = value.trim().replace(/_/g, " ");
  return titleCaseWords(normalized.split(/\s+/).filter(Boolean));
}

function formatCreatedAtLabel(createdAt: unknown): string {
  const n = typeof createdAt === "number" ? createdAt : Number(createdAt);
  if (!Number.isFinite(n) || n <= 0) return "—";
  const ms = n > 1e12 ? n : n * 1000;
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return "—";
  }
}

function eventToRow(e: unknown): BankLinkingLogRow {
  const row =
    typeof e === "object" && e !== null ? (e as Record<string, unknown>) : {};
  const statusRaw = row.status != null ? String(row.status) : "";
  const stepRaw = row.step != null ? String(row.step) : "";
  const msg =
    typeof row.message === "object" && row.message !== null
      ? (row.message as Record<string, unknown>)
      : null;
  const fromMessage = msg?.providerName ?? msg?.provider_name;
  const institution = row.institutionName;
  let providerName = "";
  if (fromMessage != null && String(fromMessage).trim() !== "") {
    providerName = String(fromMessage).trim();
  } else if (institution != null && String(institution).trim() !== "") {
    providerName = String(institution).trim();
  }
  return {
    status: statusRaw ? formatLabelFromMixedString(statusRaw) : "—",
    step: stepRaw ? formatStepLabel(stepRaw) : "—",
    providerName,
    dateTimeLabel: formatCreatedAtLabel(row.timestamp),
  };
}

function parseRetryCount(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.floor(n);
}

function deriveAttemptSummary(
  events: unknown[],
  retryCountFromApi: number | null,
): Pick<
  BankLinkingRequestGroup,
  | "pollAttempts"
  | "maxPollAttempts"
  | "successfulPolls"
  | "failedPollAttempts"
  | "resetType"
> {
  let successfulPolls = 0;
  let jobInProgressCount = 0;
  let resetType: BankLinkResetType = null;

  for (const event of events) {
    if (!event || typeof event !== "object") continue;
    const step = String((event as Record<string, unknown>).step ?? "")
      .trim()
      .toLowerCase();
    if (step === "job_success") successfulPolls += 1;
    if (step === "job_in_progress") jobInProgressCount += 1;
    if (step === "manual_bank_link_reset") resetType = "manual";
    if (step.startsWith("automated_bank_link_reset")) resetType = "automated";
  }

  const pollAttempts =
    retryCountFromApi != null
      ? retryCountFromApi
      : jobInProgressCount > 0
        ? jobInProgressCount
        : null;

  const failedPollAttempts =
    pollAttempts != null ? Math.max(0, pollAttempts - successfulPolls) : 0;

  return {
    pollAttempts,
    maxPollAttempts: MAX_POLL_ATTEMPTS,
    successfulPolls,
    failedPollAttempts,
    resetType,
  };
}

export function formatBankLinkResetTypeLabel(
  resetType: BankLinkResetType,
): string {
  if (resetType === "automated") return "Automated (hard reset at limit)";
  if (resetType === "manual") return "Manual (staff reset)";
  return "—";
}

/** One group per product request, newest first. */
export function buildBankLinkingRequestGroups(
  raw: unknown,
): BankLinkingRequestGroup[] {
  if (!Array.isArray(raw)) return [];

  const paired: { createdAt: number; group: BankLinkingRequestGroup }[] = [];
  for (const req of raw) {
    if (!req || typeof req !== "object") continue;
    const row = req as Record<string, unknown>;
    const id =
      row.product_request_id != null
        ? String(row.product_request_id).trim()
        : "";
    const events = Array.isArray(row.events_json) ? row.events_json : [];
    const eventRows = events.map((e) => eventToRow(e));
    const attemptSummary = deriveAttemptSummary(
      events,
      parseRetryCount(row.retry_count),
    );
    const statusRaw = row.status != null ? String(row.status) : "";
    const statusKey = statusRaw.trim().toLowerCase();
    const createdAt =
      typeof row.created_at === "number"
        ? row.created_at
        : Number(row.created_at) || 0;
    paired.push({
      createdAt,
      group: {
        productRequestId: id || "—",
        status: statusRaw ? formatLabelFromMixedString(statusRaw) : "—",
        statusKey,
        createdAtLabel: formatCreatedAtLabel(row.created_at),
        eventRows,
        ...attemptSummary,
      },
    });
  }
  paired.sort((a, b) => b.createdAt - a.createdAt);
  return paired.map((p) => p.group);
}

export function hasAnyBankLinkingEventRows(
  groups: BankLinkingRequestGroup[],
): boolean {
  return groups.some((g) => g.eventRows.length > 0);
}

/** Mirrors backend `IBV_FAILED_TERMINAL_STATUSES` for summary badge styling. */
export const IBV_FAILED_TERMINAL_STATUSES = new Set([
  "failed",
  "generation_failed",
  "ibv_data_acquire_failed",
  "reconnect_required",
  "ibv_request_expired",
]);

export type BankLinkingSummaryBadgeVariant =
  | "complete"
  | "failed"
  | "neutral";

export function summaryStatusBadgeVariant(
  statusKey: string,
): BankLinkingSummaryBadgeVariant {
  const k = (statusKey || "").trim().toLowerCase();
  if (k === "complete") return "complete";
  if (IBV_FAILED_TERMINAL_STATUSES.has(k)) return "failed";
  return "neutral";
}
